import { useEffect, useState } from 'react';
import { ClipboardCheck, FileSignature, MessageSquare, CalendarPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { initial, alsText } from '@/lib/text';
import { datumKurz, komma, delta } from '@/components/kundenakte/akteText';
import {
  Angebote, CoachNotiz, Leistungen, LetzterCheckup, PhaseKarte, Termine, Verlauf,
} from '@/components/kundenakte/AkteUebersicht';

/* Kundenakte-Übersicht im Stil des Mockups „Kundenakte“ (30.10.2026) — NUR für die Trainer-Webseite /coach. Das CRM
   (LeadDetailPage) bleibt bei AkteKopf/AkteUebersicht. Alles kommt aus GET /api/coach/kunden/{id}/akte; Tagesziele
   (Fortschritt), Gewohnheiten, Health-Daten und Dokumente lesen die Ernährungs-Spiegelrouten (`/x/ernaehrung/…`) und fehlen
   still, wenn der Zugang sie nicht hat. Die Seite rechnet nichts selbst. */

const KARTE = 'rounded-[14px] border border-border bg-card px-5 py-[18px]';
const H2 = 'mb-3 flex items-center gap-2 text-base font-bold';
const ZEILE = 'flex items-center justify-between gap-3 border-t border-border py-2.5 text-sm first:border-t-0';
const CHIP = 'inline-flex items-center rounded-full border border-border bg-muted px-[11px] py-[5px] text-xs font-bold';
const KNOPF = 'min-h-11 rounded-[10px] px-4 text-sm font-bold';

const PILLE = {
  mitglied: 'bg-[#e3f4ea] text-[#1f6b3d] dark:bg-[#13301f] dark:text-[#7fd1a0]',
  pausiert: 'bg-[#fbf0d3] text-[#7a5a0c] dark:bg-[#33290f] dark:text-[#f3c46b]',
  gekuendigt: 'bg-[#fbe3df] text-[#9b2d1f] dark:bg-[#3a1c18] dark:text-[#ff9a8a]',
  lead: 'bg-marke/15 text-marke',
};

/* Lädt eine Spiegelroute; Fehler (403 ohne Freigabe, 404) = still null, die Karte bleibt weg. */
function useSpiegel(coach, pfad, aktiv) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!aktiv || !pfad) { setD(null); return undefined; }
    let weg = false;
    coach.get(pfad).then((r) => { if (!weg) setD(r.data); }).catch(() => { if (!weg) setD(null); });
    return () => { weg = true; };
  }, [coach, pfad, aktiv]);
  return d;
}

export function AkteKopfCoach({ lead, akte, onCheckup, onNachricht, onTermin, onAngebot, chips }) {
  const k = akte?.kopf;
  const status = k?.status;
  const woche = akte?.phase?.status === 'laeuft' ? akte.phase.woche : null;
  const zeile = [
    k?.alter != null ? `${k.alter} Jahre` : null,
    k?.hauptziel ? `Ziel ${k.hauptziel}` : null,
    woche ? `Woche ${woche}` : null,
    k?.coach ? `Coach ${k.coach}` : null,
    lead.mitgliedsnummer ? `Nr. ${lead.mitgliedsnummer}` : null,
    k?.mitglied_seit ? `Mitglied seit ${datumKurz(k.mitglied_seit)}` : null,
  ].filter(Boolean);
  const bausteine = (k?.bausteine || []).map((b) => b.text).filter(Boolean);
  return (
    <header className={`${KARTE} flex flex-wrap items-center justify-between gap-[18px]`} data-testid="akte-kopf">
      <div className="flex min-w-0 items-center gap-3.5">
        <span aria-hidden="true" className="coach-zahl inline-flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-full bg-muted text-base font-extrabold">{initial(lead.name)}</span>
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-xl font-extrabold sm:text-[28px]">{alsText(lead.name)}</h1>
            {status && <span className={`rounded-full px-2 py-[3px] text-[11px] font-extrabold ${PILLE[status.schluessel] || PILLE.lead}`} data-testid="akte-status">{status.text}</span>}
          </div>
          {zeile.length > 0 && <div className="text-[13px] text-muted-foreground">{zeile.join(' · ')}</div>}
          {(bausteine.length > 0 || chips) && (
            <div className="flex flex-wrap gap-1.5" data-testid="akte-bausteine">
              {bausteine.map((t, i) => <span key={`${t}-${i}`} className={CHIP}>{t}</span>)}
              {chips}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" className={`${KNOPF} gap-1.5`} onClick={onNachricht} data-testid="akte-nachricht"><MessageSquare className="h-4 w-4" aria-hidden="true" /> Nachricht</Button>
        {onTermin && <Button type="button" variant="outline" className={`${KNOPF} gap-1.5`} onClick={onTermin} data-testid="akte-termin"><CalendarPlus className="h-4 w-4" aria-hidden="true" /> Termin</Button>}
        <Button type="button" variant="outline" className={`${KNOPF} gap-1.5`} onClick={onAngebot} data-testid="akte-angebot"><FileSignature className="h-4 w-4" aria-hidden="true" /> Angebot erstellen</Button>
        <Button type="button" className={`${KNOPF} gap-1.5 bg-primary text-white hover:bg-primary/90`} onClick={onCheckup} data-testid="akte-checkup-starten"><ClipboardCheck className="h-4 w-4" aria-hidden="true" /> Check-up starten</Button>
      </div>
    </header>
  );
}

function NaechsterSchritt({ schritt, onAktion }) {
  if (!schritt) return null;
  return (
    <section className="coach-hinweis flex flex-wrap items-center justify-between gap-3.5 rounded-[14px] border px-5 py-[18px]" data-testid="akte-naechster-schritt">
      <div className="min-w-0">
        <div className="text-xs font-extrabold tracking-[0.1em] text-marke">NÄCHSTER SCHRITT</div>
        <div className="coach-zahl text-lg font-bold">{schritt.titel}</div>
        <div className="coach-hinweis-text text-[13px]">{schritt.text}</div>
      </div>
      {schritt.aktion && <Button type="button" className={`${KNOPF} bg-primary text-white hover:bg-primary/90`} onClick={() => onAktion?.(schritt.aktion)} data-testid="akte-naechster-schritt-knopf">Öffnen</Button>}
    </section>
  );
}

function Kennzahl({ titel, wert, unter, testid, unterKlasse = 'text-muted-foreground' }) {
  return (
    <div className={KARTE} data-testid={testid}>
      <div className="text-[13px] text-muted-foreground">{titel}</div>
      <div className="coach-zahl truncate text-[28px] font-extrabold leading-[1.1]">{wert}</div>
      {unter && <div className={`text-[13px] ${unterKlasse}`}>{unter}</div>}
    </div>
  );
}

function Kennzahlen({ akte, plan }) {
  const k = akte.kacheln || {};
  const g = k.gewicht;
  const ds = delta(g?.delta_start_kg, 'kg');
  const dl = delta(g?.delta_letzte_kg, 'kg');
  return (
    <section className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }} data-testid="akte-kacheln">
      <Kennzahl testid="kachel-gewicht" titel="Gewicht"
        wert={!akte.mit_werten ? 'nur mit Recht' : g?.gewicht_kg != null ? `${komma(g.gewicht_kg)} kg` : '–'}
        unter={!akte.mit_werten ? 'Messwerte nur mit Recht Ernährung' : g ? (ds ? `${ds.text} seit ${datumKurz(g.start_am)}` : dl ? `${dl.text} zur vorigen` : `gemessen ${datumKurz(g.gemessen_am)}`) : 'noch keine Messung'}
        unterKlasse={(ds || dl)?.klasse} />
      {plan && plan.quote != null && (
        <div data-testid="coach-akte-plan"><Kennzahl titel="Plan eingetragen" wert={`${plan.quote} %`} unter={plan.soll != null ? `${plan.ist ?? 0} von ${plan.soll} Einheiten (${plan.wochen || 4} Wochen)` : null} /></div>
      )}
      <Kennzahl testid="kachel-ernaehrung" titel="Ernährung"
        wert={!akte.mit_ernaehrung ? 'nur mit Recht' : k.ernaehrung ? `${k.ernaehrung.im_ziel} / 7` : '–'}
        unter={!akte.mit_ernaehrung ? 'Recht Ernährung' : k.ernaehrung ? `Tage im Ziel · ${k.ernaehrung.geloggt} von 7 erfasst` : 'kein Ernährungscoaching'} />
      <Kennzahl testid="kachel-pt" titel="PT-Kontingent" wert={k.pt ? `${k.pt.uebrig} / ${k.pt.soll}` : '–'} unter={k.pt ? 'Einheiten übrig' : 'kein Coaching-Paket'} />
      <Kennzahl testid="kachel-anwesenheit" titel="Anwesenheit"
        wert={k.anwesenheit ? `${k.anwesenheit.besuche_4w}` : '–'}
        unter={k.anwesenheit ? `Besuche in 4 Wochen · Ø ${komma(k.anwesenheit.schnitt_woche)} je Woche` : 'keine laufende Mitgliedschaft'} />
      <Kennzahl testid="kachel-checkup" titel="Check-up-Kontingent"
        wert={k.checkup ? `${k.checkup.genutzt ?? 0} / ${k.checkup.inklusive ?? 0}` : '–'}
        unter={k.checkup ? `genutzt · letzter ${datumKurz(k.checkup.letzter)}` : null} />
      {akte.mit_werten && g?.koerperfett_prozent != null && (
        <Kennzahl testid="kachel-koerper" titel="Körperfett · Muskel" wert={`${komma(g.koerperfett_prozent)} %`} unter={`Muskel ${komma(g.muskelmasse_kg)} kg · ${datumKurz(g.gemessen_am)}`} />
      )}
    </section>
  );
}

function Balken({ titel, ist, soll, einheit }) {
  if (soll == null) return null;
  const p = soll > 0 && ist != null ? Math.min(100, Math.round((ist / soll) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between text-[13px]"><span>{titel}</span><span className="text-muted-foreground tabular-nums">{ist != null ? komma(ist, 0) : '–'} / {komma(soll, 0)}{einheit}</span></div>
      <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-label={titel} aria-valuenow={p} aria-valuemin={0} aria-valuemax={100}>
        <span className="coach-balken block h-full bg-marke" style={{ width: `${p}%` }} />
      </div>
    </div>
  );
}

function Tagesziele({ ziele, bilanz }) {
  const z = ziele || {};
  const soll = bilanz?.ziel || {};
  const ist = bilanz?.gegessen || {};
  const kcal = soll.kcal ?? z.kcal;
  if (kcal == null && z.schritte == null && z.wasser_l == null) return null;
  return (
    <section className={KARTE} data-testid="akte-ziele">
      <h2 className={H2}>Tagesziele heute</h2>
      <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <Balken titel="Kalorien" ist={bilanz ? ist.kcal : null} soll={kcal} einheit="" />
        <Balken titel="Protein" ist={bilanz ? ist.eiweiss : null} soll={soll.eiweiss ?? z.eiweiss} einheit=" g" />
        <Balken titel="Kohlenhydrate" ist={bilanz ? ist.kh : null} soll={soll.kh ?? z.kh} einheit=" g" />
        <Balken titel="Fett" ist={bilanz ? ist.fett : null} soll={soll.fett ?? z.fett} einheit=" g" />
        {z.wasser_l != null && <Balken titel="Wasser" ist={null} soll={z.wasser_l} einheit=" l" />}
        {z.schritte != null && <Balken titel="Schritte" ist={null} soll={z.schritte} einheit="" />}
      </div>
      {z.hauptziel && <p className="mt-3 text-[13px] text-muted-foreground">Hauptziel: {z.hauptziel}</p>}
    </section>
  );
}

const WT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const tagKurz = (iso) => WT[new Date(`${String(iso).slice(0, 10)}T12:00:00`).getDay()];

function Gewohnheiten({ d }) {
  const liste = d?.gewohnheiten || [];
  if (!liste.length) return null;
  return (
    <section className={KARTE} data-testid="akte-gewohnheiten">
      <h2 className={H2}>Gewohnheiten · letzte 7 Tage{d.treue != null && <span className="ml-auto text-[13px] font-semibold text-muted-foreground">Treue {d.treue} %</span>}</h2>
      {liste.map((g) => (
        <div key={g.id} className={ZEILE}>
          <span className="min-w-0">{g.titel}</span>
          <span className="flex shrink-0 gap-1" role="img" aria-label={`${g.titel}: ${(g.verlauf || []).filter((t) => t.erfuellt).length} von ${(g.verlauf || []).length} Tagen erfüllt`}>
            {(g.verlauf || []).map((t) => {
              const heute = t.datum === d.datum;
              const klasse = t.erfuellt ? 'bg-marke text-[#140c08]' : heute ? 'border-[1.5px] border-dashed border-marke text-marke' : 'bg-muted text-muted-foreground';
              return <span key={t.datum} className={`inline-flex h-7 w-7 items-center justify-center rounded-[7px] text-[11px] font-extrabold ${klasse}`}>{tagKurz(t.datum)}</span>;
            })}
          </span>
        </div>
      ))}
    </section>
  );
}

function Programm({ phase, onPlaene }) {
  if (!phase || phase.status === 'abgeschlossen') {
    return (
      <section className={KARTE} data-testid="akte-programm">
        <h2 className={H2}>Programm mit Phasen</h2>
        <p className="text-[13px] text-muted-foreground">Keine laufende Phase.</p>
        {onPlaene && <Button type="button" variant="outline" className={`${KNOPF} mt-3`} onClick={onPlaene}>Trainingspläne öffnen</Button>}
      </section>
    );
  }
  return (
    <section className={KARTE} data-testid="akte-programm">
      <h2 className={H2}>{phase.label || 'Programm mit Phasen'}</h2>
      <PhaseKarte phase={phase} />
      {onPlaene && <Button type="button" variant="outline" className={`${KNOPF} mt-3`} onClick={onPlaene}>Trainingspläne öffnen</Button>}
    </section>
  );
}

function Dokumente({ coach, leadId, d }) {
  const liste = d?.eintraege || [];
  const [fehler, setFehler] = useState('');
  if (!liste.length) return null;
  const ansehen = async (e) => {
    setFehler('');
    if (e.url && !e.datei) { window.open(e.url, '_blank', 'noopener,noreferrer'); return; }
    try {
      const r = await coach.get(`/x/ernaehrung/mitglied/${leadId}/tresor/${e.id}/datei`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank', 'noopener');
    } catch { setFehler('Datei nicht geladen.'); }
  };
  return (
    <section className={KARTE} data-testid="akte-dokumente">
      <h2 className={H2}>Dokumente</h2>
      {liste.slice(0, 6).map((e) => (
        <div key={e.id} className={ZEILE}>
          <span className="min-w-0"><span className="break-words">{e.titel}</span> <span className="text-xs text-muted-foreground">{e.typ} · {datumKurz(e.hochgeladen_am)}</span></span>
          <Button type="button" variant="outline" className={`${KNOPF} shrink-0`} onClick={() => ansehen(e)}>Ansehen</Button>
        </div>
      ))}
      {fehler && <p className="mt-2 text-xs text-destructive" role="alert">{fehler}</p>}
    </section>
  );
}

const minutenAlsStunden = (min) => (min == null ? null : `${komma(min / 60)} h`);

function Health({ d }) {
  const t = (d?.tage || []).find((x) => x.schritte != null || x.schlaf_min != null || x.body_battery_morgens != null);
  if (!d?.verbindung || !t) return null;
  const signale = d.signale || [];
  return (
    <section className={KARTE} data-testid="akte-health">
      <h2 className={H2}>Health-Daten <span className="ml-auto text-[13px] font-semibold text-muted-foreground">{datumKurz(t.datum)}</span></h2>
      {t.schritte != null && <div className={ZEILE}><span>Schritte · Garmin</span><strong className="tabular-nums">{komma(t.schritte, 0)}</strong></div>}
      {t.schlaf_min != null && <div className={ZEILE}><span>Schlaf · Garmin</span><strong className="tabular-nums">{minutenAlsStunden(t.schlaf_min)}</strong></div>}
      {(t.body_battery_morgens != null || signale.length > 0) && (
        <div className={ZEILE}>
          <span>Erholung · Garmin</span>
          {signale.length > 0
            ? <strong className="text-[#7a5a0c] dark:text-[#f3c46b]">{signale[0].name || 'Signal'}</strong>
            : <strong className="tabular-nums">Body Battery {t.body_battery_morgens}</strong>}
        </div>
      )}
    </section>
  );
}

function ProfilEinstellungen({ akte, onProfil }) {
  const z = akte.ziele || {};
  const k = akte.kopf || {};
  const zeilen = [
    ['Hauptziel', k.hauptziel || z.hauptziel],
    ['Coach', k.coach],
    ['Tarif', k.tarif],
    ['Schrittziel', z.schritte != null ? komma(z.schritte, 0) : null],
  ].filter(([, w]) => w);
  return (
    <section className={KARTE} data-testid="akte-profil-einstellungen">
      <h2 className={H2}>Profil &amp; Einstellungen</h2>
      {zeilen.map(([t, w]) => <div key={t} className={ZEILE}><span>{t}</span><span className="text-[13px] text-muted-foreground">{w}</span></div>)}
      {onProfil && <Button type="button" variant="outline" className={`${KNOPF} mt-3`} onClick={onProfil}>Profil öffnen</Button>}
    </section>
  );
}

export default function CoachAkteUebersicht({ coach, leadId, akte, plan, aktiv, onAktion, onPlaene, onProfil, client, basis = '/kunden' }) {
  const bilanzD = useSpiegel(coach, `/x/ernaehrung/mitglied/${leadId}/protokoll`, aktiv);
  const gewD = useSpiegel(coach, `/x/ernaehrung/mitglied/${leadId}/gewohnheiten`, aktiv);
  const healthD = useSpiegel(coach, `/x/ernaehrung/mitglied/${leadId}/wearable?tage=7`, aktiv);
  const docsD = useSpiegel(coach, `/x/ernaehrung/mitglied/${leadId}/tresor`, aktiv);
  const karte = (titel, testid, kinder) => <section className={KARTE} data-testid={testid}><h2 className={H2}>{titel}</h2>{kinder}</section>;
  return (
    <div className="space-y-[18px]" data-testid="akte-uebersicht">
      <NaechsterSchritt schritt={akte.naechster_schritt} onAktion={onAktion} />
      <Kennzahlen akte={akte} plan={plan} />
      <div className="flex flex-wrap items-start gap-[18px]">
        <div className="flex min-w-0 flex-[2_1_460px] flex-col gap-[18px]">
          <Tagesziele ziele={akte.ziele} bilanz={bilanzD?.bilanz} />
          <Gewohnheiten d={gewD} />
          <Programm phase={akte.phase} onPlaene={onPlaene} />
          {karte('Aktive Leistungen', 'akte-leistungen', <Leistungen liste={akte.aktive_leistungen} />)}
          {akte.angebote_offen?.length > 0 && <div id="akte-angebote" className="scroll-mt-4">{karte('Offene Angebote (in der App)', 'akte-angebote', <Angebote liste={akte.angebote_offen} />)}</div>}
          <Dokumente coach={coach} leadId={leadId} d={docsD} />
        </div>
        <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-[18px]">
          <Health d={healthD} />
          {karte('Letzter Check-up', 'akte-letzter-checkup', <LetzterCheckup c={akte.letzter_checkup} mitWerten={akte.mit_werten} mitBefund={akte.mit_befund ?? akte.mit_werten} />)}
          {karte('Kommende Termine', 'akte-termine', <Termine liste={akte.termine} />)}
          <ProfilEinstellungen akte={akte} onProfil={onProfil} />
          <section className={KARTE} data-testid="akte-notiz">
            <h2 className={H2}>Coach-Notiz <span className="ml-auto text-[13px] font-semibold text-muted-foreground">privat</span></h2>
            <CoachNotiz leadId={leadId} notiz={akte.coach_notiz} client={client} basis={basis} />
          </section>
          {karte('Verlauf', 'akte-verlauf', <Verlauf liste={akte.verlauf} />)}
        </div>
      </div>
    </div>
  );
}
